<template>
  <div class="spot-strategy-picker" data-testid="strategy-picker">
    <!-- Editable Mode -->
    <div v-if="mode === 'editable'">
      <p class="form-label fs-9 fw-bolder p-0 text-body mb-2 text-uppercase">
        {{ $t("setPriceTerms.selectStrategyLabel") }}
      </p>

      <div class="mb-4">
        <!-- Market Strategy Radio -->
        <div class="form-check mb-2">
          <input
            class="form-check-input"
            type="radio"
            name="strategySelection"
            id="marketStrategy"
            value="market"
            v-model="selectedStrategy"
            data-testid="market-strategy-radio"
          />
          <label
            class="form-check-label fs-8 d-flex align-items-center"
            for="marketStrategy"
          >
            {{ $t("setPriceTerms.automaticHighBid") }}
            <InfoTooltip
              :text="$t('common.click_to_learn_more')"
              :showIcon="false"
            >
              <button
                type="button"
                class="btn btn-link p-0 ms-2"
                @click.prevent="showMarketInfoModal"
                data-testid="market-info-button"
              >
                <font-awesome-icon
                  :icon="'fas fa-info-circle'"
                  class="text-secondary-emphasis"
                />
              </button>
            </InfoTooltip>
          </label>
        </div>

        <!-- Minimum Strategy Radio -->
        <div class="form-check">
          <input
            class="form-check-input"
            type="radio"
            name="strategySelection"
            id="minimumStrategy"
            value="minimum"
            v-model="selectedStrategy"
            data-testid="minimum-strategy-radio"
          />
          <label
            class="form-check-label fs-8 d-flex align-items-center"
            for="minimumStrategy"
          >
            {{ $t("setPriceTerms.fresh_prod_above_min_price") }}
            <InfoTooltip
              :text="$t('common.click_to_learn_more')"
              :showIcon="false"
            >
              <button
                type="button"
                class="btn btn-link p-0 ms-2"
                @click.prevent="showMinimumInfoModal"
                data-testid="minimum-info-button"
              >
                <font-awesome-icon
                  :icon="'fas fa-info-circle'"
                  class="text-secondary-emphasis"
                />
              </button>
            </InfoTooltip>
          </label>
        </div>

        <!-- Minimum Price Configuration -->
        <template v-if="selectedStrategy === 'minimum'">
          <div class="mt-4">
            <!-- Price and Duration Inputs -->
            <div class="row mb-4">
              <div class="mb-2 mb-md-0 col-12 col-md-6">
                <label for="minimumPrice" class="form-label fs-8 p-0 text-body">
                  {{ $t("setPriceTerms.minimumPriceLabel") }}
                </label>
                <div class="input-group">
                  <input
                    type="number"
                    class="form-control"
                    :class="{ 'is-invalid': !isMinimumPriceValid }"
                    id="minimumPrice"
                    v-model.number="minimumPriceValue"
                    :placeholder="$t('setPriceTerms.enterPrice')"
                    min="0"
                    step="0.01"
                    data-testid="minimum-price-input"
                  />
                  <span class="input-group-text"> € / MWh </span>
                </div>
                <div
                  v-if="!isMinimumPriceValid && validationError"
                  class="invalid-feedback d-block"
                  data-testid="validation-error"
                >
                  {{ validationError }}
                </div>
              </div>

              <div class="col-12 col-md-6">
                <label
                  for="durationMonths"
                  class="form-label fs-8 p-0 text-body"
                >
                  {{ $t("setPriceTerms.duration.minimum_price_window") }}
                </label>
                <select
                  class="form-control"
                  :class="{ 'is-invalid': !isDurationValid }"
                  id="durationMonths"
                  v-model.number="durationMonths"
                  data-testid="duration-selector"
                >
                  <option
                    v-for="month in [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]"
                    :key="month"
                    :value="month"
                  >
                    {{ month }}
                    {{ month === 1 ? $t("common.month") : $t("common.months") }}
                  </option>
                </select>
                <div class="form-text text-muted fs-9">
                  {{ $t("setPriceTerms.duration.default_hint") }}
                </div>
                <div
                  v-if="!isDurationValid && durationValidationError"
                  class="invalid-feedback d-block"
                  data-testid="duration-validation-error"
                >
                  {{ durationValidationError }}
                </div>
              </div>
            </div>

            <!-- Two-Phase Timeline Visual -->
            <div class="phases-timeline mb-3" data-testid="phases-timeline">
              <p
                class="form-label fs-9 fw-bolder p-0 text-body mb-2 text-uppercase"
              >
                {{ $t("setPriceTerms.phases.title") }}
              </p>

              <!-- Phase 1 -->
              <div class="d-flex align-items-start mb-2">
                <div class="phase-indicator phase-1-indicator me-3">
                  <div class="phase-dot bg-primary"></div>
                  <div class="phase-line bg-primary"></div>
                </div>
                <div class="flex-grow-1">
                  <div class="d-flex align-items-center mb-1">
                    <span
                      class="badge bg-primary-subtle text-primary fw-semibold me-2"
                    >
                      {{ $t("setPriceTerms.phases.phase_1_badge") }}
                    </span>
                    <span class="fw-semibold fs-8 text-dark">
                      {{
                        $t("setPriceTerms.phases.phase_1_title", {
                          duration: durationMonths,
                        })
                      }}
                    </span>
                  </div>
                  <p class="text-muted fs-9 mb-0">
                    {{
                      $t("setPriceTerms.phases.phase_1_description", {
                        price: formattedMinPrice,
                      })
                    }}
                  </p>
                </div>
              </div>

              <!-- Phase 2 -->
              <div class="d-flex align-items-start">
                <div class="phase-indicator me-3">
                  <div class="phase-dot bg-success"></div>
                </div>
                <div class="flex-grow-1">
                  <div class="d-flex align-items-center mb-1">
                    <span
                      class="badge bg-success-subtle text-success fw-semibold me-2"
                    >
                      {{ $t("setPriceTerms.phases.phase_2_badge") }}
                    </span>
                    <span class="fw-semibold fs-8 text-dark">
                      {{
                        $t("setPriceTerms.phases.phase_2_title", {
                          duration: durationMonths,
                        })
                      }}
                    </span>
                  </div>
                  <p class="text-muted fs-9 mb-0">
                    {{ $t("setPriceTerms.phases.phase_2_description") }}
                  </p>
                </div>
              </div>
            </div>

            <DurationWarnings :durationMonths="durationMonths" />
          </div>
        </template>
      </div>
    </div>

    <!-- Readonly Mode -->
    <div v-else data-testid="readonly-strategy-display">
      <div class="d-flex align-items-center justify-content-between">
        <div>
          <span v-if="selectedStrategy === 'market'">
            {{ $t("setPriceTerms.automaticHighBid") }}
          </span>
          <div v-else>
            <div class="fw-semibold">
              {{ $t("setPriceTerms.fresh_prod_above_min_price") }}
              <span v-if="minimumPriceValue">
                - {{ minimumPriceValue.toFixed(2) }} € / MWh
              </span>
            </div>
            <div class="small text-muted mt-1">
              {{
                $t("setPriceTerms.phases.readonly_summary", {
                  duration: durationMonths,
                })
              }}
            </div>
          </div>
        </div>
        <div>
          <InfoTooltip
            :text="$t('common.click_to_learn_more')"
            :showIcon="false"
          >
            <button
              v-if="selectedStrategy === 'market'"
              type="button"
              class="btn btn-link p-0"
              @click.prevent="showMarketInfoModal"
              data-testid="market-info-button"
            >
              <font-awesome-icon
                :icon="'fas fa-info-circle'"
                class="text-secondary-emphasis"
              />
            </button>
            <button
              v-else
              type="button"
              class="btn btn-link p-0"
              @click.prevent="showMinimumInfoModal"
              data-testid="minimum-info-button"
            >
              <font-awesome-icon
                :icon="'fas fa-info-circle'"
                class="text-secondary-emphasis"
              />
            </button>
          </InfoTooltip>
        </div>
      </div>
    </div>

    <!-- Info Modals -->
    <MarketPriceInfoModal ref="marketPriceModal" />
    <MinimumPriceInfoModal ref="minimumPriceModal" />
  </div>
</template>

<script setup lang="ts">
import InfoTooltip from "@/components/common/InfoTooltip.vue";
import { SpotStrategy } from "@/composables/useCompanySpotStrategy";
import { useValidators } from "@/composables/useValidators";
import DurationWarnings from "@/dashboard/common/DurationWarnings.vue";
import MarketPriceInfoModal from "@/dashboard/common/MarketPriceInfoModal.vue";
import MinimumPriceInfoModal from "@/dashboard/common/MinimumPriceInfoModal.vue";
import { useVuelidate } from "@vuelidate/core";
import { computed, ref, toRefs, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";

interface SpotStrategyPickerProps {
  mode: "editable" | "readonly";
  initialStrategy: SpotStrategy;
}

interface StrategyChangedEvent {
  type: "market" | "minimum";
  minimumPrice?: number;
  durationMonths?: number;
  isValid: boolean;
}

const props = defineProps<SpotStrategyPickerProps>();
const emit = defineEmits<{
  "update:strategy": [event: StrategyChangedEvent];
}>();

const { mode, initialStrategy } = toRefs(props);
const { t } = useI18n();
const validators = useValidators(t);

// Reactive state
const selectedStrategy = ref<SpotStrategy["type"]>("market");
const minimumPriceValue = ref<number>(1);
const previousMinimumPrice = ref<number>(1);
const durationMonths = ref<number>(6);
const previousDurationMonths = ref<number>(6);

// Validation setup
const validationRules = computed(() => {
  if (selectedStrategy.value !== "minimum") return {};

  return {
    minimumPriceValue: {
      required: validators.required,
      min: validators.min(0.01),
    },
    durationMonths: {
      required: validators.required,
      min: validators.min(1),
      max: validators.max(12),
    },
  };
});

const validationState = computed(() => ({
  minimumPriceValue: minimumPriceValue.value,
  durationMonths: durationMonths.value,
}));

const v$ = useVuelidate(validationRules, validationState);

// Modal refs
const marketPriceModal =
  useTemplateRef<InstanceType<typeof MarketPriceInfoModal>>("marketPriceModal");
const minimumPriceModal =
  useTemplateRef<InstanceType<typeof MinimumPriceInfoModal>>(
    "minimumPriceModal"
  );

const formattedMinPrice = computed(() => {
  const price = minimumPriceValue.value;
  if (price !== undefined && typeof price === "number" && !isNaN(price)) {
    return price.toFixed(2);
  }
  return "0.00";
});

// Computed validation using Vuelidate
const isMinimumPriceValid = computed(() => {
  if (selectedStrategy.value !== "minimum") return true;
  return !v$.value.minimumPriceValue?.$invalid;
});

const isDurationValid = computed(() => {
  if (selectedStrategy.value !== "minimum") return true;
  return !v$.value.durationMonths?.$invalid;
});

const isFormValid = computed(() => {
  if (selectedStrategy.value !== "minimum") return true;
  return isMinimumPriceValid.value && isDurationValid.value;
});

const validationError = computed(() => {
  if (selectedStrategy.value !== "minimum") return null;

  const priceField = v$.value.minimumPriceValue;
  if (priceField && priceField.$errors && priceField.$errors.length > 0) {
    return priceField.$errors[0].$message;
  }

  return null;
});

const durationValidationError = computed(() => {
  if (selectedStrategy.value !== "minimum") return null;

  const durationField = v$.value.durationMonths;
  if (
    durationField &&
    durationField.$errors &&
    durationField.$errors.length > 0
  ) {
    return durationField.$errors[0].$message;
  }

  return null;
});

// Initialize state
const initializeState = () => {
  selectedStrategy.value = initialStrategy.value.type;

  if (initialStrategy.value.type === "minimum") {
    if (initialStrategy.value.minimumPrice !== undefined) {
      minimumPriceValue.value = initialStrategy.value.minimumPrice;
      previousMinimumPrice.value = initialStrategy.value.minimumPrice;
    } else {
      minimumPriceValue.value = 1;
      previousMinimumPrice.value = 1;
    }

    if (initialStrategy.value.durationMonths !== undefined) {
      durationMonths.value = initialStrategy.value.durationMonths;
      previousDurationMonths.value = initialStrategy.value.durationMonths;
    } else {
      durationMonths.value = 6;
      previousDurationMonths.value = 6;
    }
  } else {
    minimumPriceValue.value = 1;
    previousMinimumPrice.value = 1;
    durationMonths.value = 6;
    previousDurationMonths.value = 6;
  }
};

// Emit strategy changed event
const emitStrategyChanged = () => {
  const event: StrategyChangedEvent = {
    type: selectedStrategy.value,
    minimumPrice:
      selectedStrategy.value === "minimum"
        ? minimumPriceValue.value
        : undefined,
    durationMonths:
      selectedStrategy.value === "minimum" ? durationMonths.value : undefined,
    isValid: selectedStrategy.value === "market" ? true : isFormValid.value,
  };
  emit("update:strategy", event);
};

// Watch for strategy changes
watch(selectedStrategy, (newStrategy) => {
  if (newStrategy === "minimum") {
    // Restore previous values when switching back to minimum
    minimumPriceValue.value = previousMinimumPrice.value;
    durationMonths.value = previousDurationMonths.value;
    // Trigger validation for the restored values
    v$.value.minimumPriceValue?.$touch();
    v$.value.durationMonths?.$touch();
  } else {
    // Reset validation when switching away from minimum
    v$.value.$reset();
  }
  emitStrategyChanged();
});

// Watch for minimum price changes
watch(minimumPriceValue, (newPrice) => {
  if (selectedStrategy.value === "minimum") {
    // Trigger validation
    v$.value.minimumPriceValue?.$touch();

    // Remember valid prices for later use
    if (newPrice > 0) {
      previousMinimumPrice.value = newPrice;
    }
    emitStrategyChanged();
  }
});

// Watch for duration changes
watch(durationMonths, (newDuration) => {
  if (selectedStrategy.value === "minimum") {
    // Trigger validation
    v$.value.durationMonths?.$touch();

    // Remember valid durations for later use
    if (newDuration >= 1 && newDuration <= 12) {
      previousDurationMonths.value = newDuration;
    }
    emitStrategyChanged();
  }
});

// Modal methods
const showMarketInfoModal = () => {
  if (marketPriceModal.value) {
    marketPriceModal.value.toggleModal();
  }
};

const showMinimumInfoModal = () => {
  if (minimumPriceModal.value) {
    minimumPriceModal.value.toggleModal();
  }
};

// Watch for prop changes
watch(
  () => [initialStrategy.value],
  () => {
    initializeState();
    emitStrategyChanged();
  },
  { immediate: true }
);
</script>

<style scoped lang="scss">
.spot-strategy-picker {
  .form-label {
    text-transform: none;
  }
}

.phase-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 16px;
  padding-top: 4px;
}

.phase-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}

.phase-line {
  width: 2px;
  flex-grow: 1;
  min-height: 32px;
  margin-top: 4px;
  opacity: 0.3;
}
</style>
